import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Edit3, Eye, History, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import http from "../api/http";
import Modal from "../components/Modal";
import { consumirIntencionNuevo } from "../utils/navigationIntent";

const productoVacio = () => ({
  productoCatalogoId: "",
  nombre: "",
  codigo: "",
  unidadMedida: "Unidad",
  precioCompraPorUnidad: "",
  estadoProducto: "MATERIAPRIMA",
  activo: true,
});

const materialVacio = () => ({
  materialProduccionId: "",
  nombre: "",
  unidadMedida: "unidad",
  precioCompraPorUnidad: "",
  activo: true,
});

const estadoInicial = () => ({
  razonSocial: "",
  nombreFantasia: "",
  tipoDocumento: "CUIT",
  numeroDocumento: "",
  telefono: "",
  contacto: "",
  telefonoContacto: "",
  email: "",
  direccion: "",
  ciudad: "",
  provincia: "",
  observaciones: "",
  productos: [productoVacio()],
  materiales: [materialVacio()],
  activo: true,
});

const estadoCatalogoAProveedor = (producto) =>
  (producto.tipo || producto.estado) === "Materia prima" || producto.estado === "materia prima"
    ? "MATERIAPRIMA"
    : "VENTA";
const getOptionValue = (option) => (typeof option === "string" ? option : option.value);
const getOptionLabel = (option) => (typeof option === "string" ? option : option.label);
const idCatalogoDe = (producto) => {
  const referencia = producto?.productoCatalogoId;
  return String(typeof referencia === "object" ? referencia?._id || "" : referencia || "");
};
const idMaterialDe = (material) => {
  const referencia = material?.materialProduccionId;
  return String(typeof referencia === "object" ? referencia?._id || "" : referencia || "");
};
const idReferenciaDe = (referencia) =>
  String(typeof referencia === "object" ? referencia?._id || "" : referencia || "");
const normalizarTexto = (valor) => String(valor || "").trim().toLowerCase();
const unidadesCosto = {
  mg: { tipo: "Peso", unidadBase: "kg", factor: 0.000001 },
  g: { tipo: "Peso", unidadBase: "kg", factor: 0.001 },
  kg: { tipo: "Peso", unidadBase: "kg", factor: 1 },
  ml: { tipo: "Volumen", unidadBase: "L", factor: 0.001 },
  l: { tipo: "Volumen", unidadBase: "L", factor: 1 },
};
const convertirUnidad = (cantidad, unidadOrigen, unidadDestino) => {
  const origen = unidadesCosto[normalizarTexto(unidadOrigen)];
  const destino = unidadesCosto[normalizarTexto(unidadDestino)];
  if (!origen || !destino || origen.tipo !== destino.tipo) return null;
  return (Number(cantidad) * origen.factor) / destino.factor;
};
const formatearCantidad = (cantidad, maximoDecimales = 6) =>
  Number(cantidad || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maximoDecimales,
  });
const unidadCostoDe = (productoCatalogo, productoProveedor) => {
  if (productoCatalogo?.tipoMedida === "Volumen") return "L";
  if (["ml", "l"].includes(String(productoProveedor?.unidadMedida || "").toLowerCase())) return "L";
  return "kg";
};
const formatearPrecio = (precio) => {
  const valor = Number(precio);
  if (!Number.isFinite(valor) || valor <= 0) return "Sin precio";
  return valor.toLocaleString("es-AR", { style: "currency", currency: "ARS" });
};
const formatearImporte = (precio) =>
  Number(precio || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });
const formatearVariacion = (valor) => {
  const numero = Number(valor || 0);
  const importe = Math.abs(numero).toLocaleString("es-AR", { style: "currency", currency: "ARS" });
  return `${numero > 0 ? "+" : numero < 0 ? "-" : ""}${importe}`;
};
const fechaLocalClave = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};
const formatearFechaHora = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("es-AR");
};

const actualizarProductoProveedor = (producto, name, value, catalogo) => {
  if (name !== "productoCatalogoId") {
    return { ...producto, [name]: value };
  }

  const seleccionado = catalogo.find((item) => item._id === value);
  if (!seleccionado) return { ...producto, productoCatalogoId: value };

  return {
    ...producto,
    productoCatalogoId: value,
    nombre: seleccionado.nombre || producto.nombre,
    codigo: seleccionado.codigoSku || producto.codigo,
    unidadMedida: seleccionado.unidadMedida || "Unidad",
    precioCompraPorUnidad: "",
    estadoProducto: estadoCatalogoAProveedor(seleccionado),
    activo: true,
  };
};

export default function Proveedores() {
  const [proveedores, setProveedores] = useState([]);
  const [productosCatalogo, setProductosCatalogo] = useState([]);
  const [materialesCatalogo, setMaterialesCatalogo] = useState([]);
  const [formulario, setFormulario] = useState(estadoInicial);
  const [proveedorEditando, setProveedorEditando] = useState(null);
  const [proveedorDetalle, setProveedorDetalle] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [comparadorAbierto, setComparadorAbierto] = useState(false);
  const [productoCompararId, setProductoCompararId] = useState("");
  const [cantidadSimulacion, setCantidadSimulacion] = useState("1");
  const [margenSimulacion, setMargenSimulacion] = useState("0");
  const [proveedoresSimulacion, setProveedoresSimulacion] = useState({});
  const [historialPrecio, setHistorialPrecio] = useState(null);
  const [historialCargando, setHistorialCargando] = useState(false);
  const [historialError, setHistorialError] = useState("");
  const [filtroHistorial, setFiltroHistorial] = useState({ desde: "", hasta: "" });
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const obtenerProveedores = async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.get("/proveedores");
      setProveedores(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los proveedores.");
    } finally {
      setCargando(false);
    }
  };

  const obtenerProductosCatalogo = async () => {
    try {
      const { data } = await http.get("/productos?activo=true");
      setProductosCatalogo(Array.isArray(data) ? data : []);
    } catch {
      setProductosCatalogo([]);
    }
  };

  const obtenerMaterialesCatalogo = async () => {
    try {
      const { data } = await http.get("/materiales-produccion");
      setMaterialesCatalogo(Array.isArray(data) ? data : []);
    } catch {
      setMaterialesCatalogo([]);
    }
  };

  useEffect(() => {
    obtenerProveedores();
    obtenerProductosCatalogo();
    obtenerMaterialesCatalogo();
    if (consumirIntencionNuevo()) setModalAbierto(true);
  }, []);

  const proveedoresFiltrados = useMemo(() => {
    const query = busqueda.toLowerCase().trim();
    if (!query) return proveedores;

    return proveedores.filter((proveedor) => {
      const productos = (proveedor.productos || []).map((producto) => producto.nombre).join(" ");
      const materiales = (proveedor.materiales || []).map((material) => material.nombre).join(" ");
      const texto = `${proveedor.razonSocial || ""} ${proveedor.contacto || ""} ${
        proveedor.numeroDocumento || ""
      } ${proveedor.ciudad || ""} ${productos} ${materiales}`.toLowerCase();

      return texto.includes(query);
    });
  }, [proveedores, busqueda]);

  const productoComparado = useMemo(
    () => productosCatalogo.find((producto) => String(producto._id) === String(productoCompararId)) || null,
    [productosCatalogo, productoCompararId],
  );

  const comparacionProveedores = useMemo(() => {
    if (!productoComparado) return [];
    const nombre = String(productoComparado.nombre || "").trim().toLowerCase();
    const codigo = String(productoComparado.codigoSku || "").trim().toLowerCase();

    return proveedores
      .map((proveedor) => {
        const asociado = (proveedor.productos || []).find((producto) => {
          if (producto.activo === false) return false;
          if (idCatalogoDe(producto) === String(productoComparado._id)) return true;
          return (
            (codigo && String(producto.codigo || "").trim().toLowerCase() === codigo) ||
            (nombre && String(producto.nombre || "").trim().toLowerCase() === nombre)
          );
        });
        return asociado ? { proveedor, producto: asociado } : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        const precioA = Number(a.producto.precioCompraPorUnidad ?? a.producto.precioPorKg ?? 0);
        const precioB = Number(b.producto.precioCompraPorUnidad ?? b.producto.precioPorKg ?? 0);
        if (precioA <= 0 && precioB > 0) return 1;
        if (precioB <= 0 && precioA > 0) return -1;
        return precioA - precioB || a.proveedor.razonSocial.localeCompare(b.proveedor.razonSocial);
      });
  }, [productoComparado, proveedores]);

  const simulacionReceta = useMemo(() => {
    if (!productoComparado || !["Intermedio", "Final"].includes(productoComparado.tipo)) return null;

    const rendimiento = productoComparado.recetaRendimiento || {};
    const cantidadRendimiento = Number(rendimiento.cantidad || 0);
    const cantidadSolicitada = Number(cantidadSimulacion || 0);
    const productosPorId = new Map(productosCatalogo.map((producto) => [String(producto._id), producto]));
    const productosPorNombre = new Map(
      productosCatalogo.map((producto) => [normalizarTexto(producto.nombre), producto]),
    );
    const componentes = new Map();
    const errores = new Set();

    const agregarComponente = ({ tipo, id, nombre, cantidad, unidad }) => {
      const clave = `${tipo}:${id || normalizarTexto(nombre)}`;
      const existente = componentes.get(clave);
      if (existente) {
        existente.cantidad += cantidad;
        return;
      }
      componentes.set(clave, { clave, tipo, id: String(id || ""), nombre, cantidad, unidad });
    };

    const expandirProducto = (producto, cantidadRecetas, recorrido = []) => {
      const productoId = String(producto?._id || normalizarTexto(producto?.nombre));
      if (recorrido.includes(productoId)) {
        errores.add(`La receta contiene una referencia circular en ${producto?.nombre || "un producto"}.`);
        return;
      }

      (producto.materialesProduccion || []).forEach((material) => {
        agregarComponente({
          tipo: "material",
          id: idReferenciaDe(material.materialId),
          nombre: material.nombre,
          cantidad: Number(material.cantidad || 0) * cantidadRecetas,
          unidad: "unidad",
        });
      });

      (producto.receta || []).forEach((ingrediente) => {
        const cantidadIngrediente = Number(ingrediente.cantidad || 0) * cantidadRecetas;
        if (cantidadIngrediente <= 0) return;

        if (ingrediente.tipo === "Materia prima") {
          const unidad = unidadesCosto[normalizarTexto(ingrediente.unidadMedida)]?.unidadBase;
          const cantidadBase = unidad
            ? convertirUnidad(cantidadIngrediente, ingrediente.unidadMedida, unidad)
            : null;
          if (cantidadBase === null) {
            errores.add(`No se pudo convertir la unidad de ${ingrediente.producto}.`);
            return;
          }
          agregarComponente({
            tipo: "ingrediente",
            id: idReferenciaDe(ingrediente.productoId),
            nombre: ingrediente.producto,
            cantidad: cantidadBase,
            unidad,
          });
          return;
        }

        const intermedio =
          productosPorId.get(idReferenciaDe(ingrediente.productoId)) ||
          productosPorNombre.get(normalizarTexto(ingrediente.producto));
        if (!intermedio) {
          errores.add(`No se encontro la receta del intermedio ${ingrediente.producto}.`);
          return;
        }

        const rendimientoIntermedio = intermedio.recetaRendimiento || {};
        const cantidadConvertida = convertirUnidad(
          cantidadIngrediente,
          ingrediente.unidadMedida,
          rendimientoIntermedio.unidadMedida,
        );
        if (cantidadConvertida === null || Number(rendimientoIntermedio.cantidad || 0) <= 0) {
          errores.add(`El rendimiento de ${intermedio.nombre} no permite calcular su costo.`);
          return;
        }
        expandirProducto(
          intermedio,
          cantidadConvertida / Number(rendimientoIntermedio.cantidad),
          [...recorrido, productoId],
        );
      });
    };

    if (cantidadRendimiento <= 0 || cantidadSolicitada <= 0) {
      if (cantidadRendimiento <= 0) errores.add("El producto no tiene un rendimiento de receta valido.");
    } else {
      expandirProducto(productoComparado, cantidadSolicitada / cantidadRendimiento);
    }

    const lineas = [...componentes.values()].map((componente) => {
      const opciones = proveedores
        .map((proveedor) => {
          const asociado =
            componente.tipo === "material"
              ? (proveedor.materiales || []).find((material) => {
                  if (material.activo === false) return false;
                  return (
                    (componente.id && idMaterialDe(material) === componente.id) ||
                    normalizarTexto(material.nombre) === normalizarTexto(componente.nombre)
                  );
                })
              : (proveedor.productos || []).find((producto) => {
                  if (producto.activo === false) return false;
                  return (
                    (componente.id && idCatalogoDe(producto) === componente.id) ||
                    normalizarTexto(producto.nombre) === normalizarTexto(componente.nombre)
                  );
                });
          if (!asociado) return null;
          return {
            proveedor,
            precio: Number(
              asociado.precioCompraPorUnidad ??
                (componente.tipo === "ingrediente" ? asociado.precioPorKg : 0) ??
                0,
            ),
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (a.precio <= 0 && b.precio > 0) return 1;
          if (b.precio <= 0 && a.precio > 0) return -1;
          return a.precio - b.precio || a.proveedor.razonSocial.localeCompare(b.proveedor.razonSocial);
        });

      const proveedorSeleccionadoId = proveedoresSimulacion[componente.clave];
      const seleccionada =
        opciones.find(({ proveedor }) => String(proveedor._id) === String(proveedorSeleccionadoId)) ||
        opciones[0] ||
        null;
      return {
        ...componente,
        opciones,
        seleccionada,
        costoTotal: componente.cantidad * Number(seleccionada?.precio || 0),
      };
    });

    const costoIngredientes = lineas
      .filter((linea) => linea.tipo === "ingrediente")
      .reduce((total, linea) => total + linea.costoTotal, 0);
    const costoMateriales = lineas
      .filter((linea) => linea.tipo === "material")
      .reduce((total, linea) => total + linea.costoTotal, 0);
    const faltantes = lineas.filter((linea) => !linea.seleccionada || linea.seleccionada.precio <= 0);
    const costoTotal = costoIngredientes + costoMateriales;
    const margenGananciaPorcentaje = Math.max(Number(margenSimulacion || 0), 0);
    const importeMargen = costoTotal * (margenGananciaPorcentaje / 100);

    return {
      lineas,
      errores: [...errores],
      faltantes,
      costoIngredientes,
      costoMateriales,
      costoTotal,
      margenGananciaPorcentaje,
      importeMargen,
      precioVentaCalculado: costoTotal + importeMargen,
      cantidadRecetas: cantidadRendimiento > 0 ? cantidadSolicitada / cantidadRendimiento : 0,
      rendimiento,
    };
  }, [
    cantidadSimulacion,
    margenSimulacion,
    productoComparado,
    productosCatalogo,
    proveedores,
    proveedoresSimulacion,
  ]);

  const historialFiltrado = useMemo(() => {
    const historial = historialPrecio?.historial || [];
    return historial.filter((movimiento) => {
      const fecha = fechaLocalClave(movimiento.fechaCambio || movimiento.createdAt);
      return (
        (!filtroHistorial.desde || fecha >= filtroHistorial.desde) &&
        (!filtroHistorial.hasta || fecha <= filtroHistorial.hasta)
      );
    });
  }, [historialPrecio, filtroHistorial]);

  const abrirHistorialPrecio = async (proveedor, producto) => {
    setHistorialPrecio({
      proveedor: { _id: proveedor._id, razonSocial: proveedor.razonSocial },
      producto: { _id: producto._id, nombre: producto.nombre },
      historial: [],
    });
    setFiltroHistorial({ desde: "", hasta: "" });
    setHistorialError("");
    setHistorialCargando(true);
    try {
      const { data } = await http.get(
        `/proveedores/${proveedor._id}/productos/${producto._id}/historial-precios`,
      );
      setHistorialPrecio(data);
    } catch (err) {
      setHistorialError(err.response?.data?.error || "No se pudo cargar el historial de precios.");
    } finally {
      setHistorialCargando(false);
    }
  };

  const limpiarMensajes = () => {
    setMensaje("");
    setError("");
  };

  const abrirCrear = () => {
    limpiarMensajes();
    setProveedorEditando(null);
    setFormulario(estadoInicial());
    setModalAbierto(true);
  };

  const abrirEditar = (proveedor) => {
    limpiarMensajes();
    setProveedorEditando(proveedor);
    setFormulario({
      ...estadoInicial(),
      ...proveedor,
      productos:
        proveedor.productos?.length > 0
              ? proveedor.productos.map((producto) => ({
              _id: producto._id,
              productoCatalogoId: producto.productoCatalogoId || "",
              nombre: producto.nombre || "",
              codigo: producto.codigo || "",
              unidadMedida: producto.unidadMedida || "Unidad",
              precioCompraPorUnidad: producto.precioCompraPorUnidad ?? producto.precioPorKg ?? "",
              estadoProducto: producto.estadoProducto || "MATERIAPRIMA",
              activo: producto.activo ?? true,
            }))
          : [productoVacio()],
      materiales:
        proveedor.materiales?.length > 0
          ? proveedor.materiales.map((material) => ({
              _id: material._id,
              materialProduccionId: material.materialProduccionId || "",
              nombre: material.nombre || "",
              unidadMedida: material.unidadMedida || "unidad",
              precioCompraPorUnidad: material.precioCompraPorUnidad ?? "",
              activo: material.activo ?? true,
            }))
          : [materialVacio()],
      activo: proveedor.activo ?? true,
    });
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setProveedorEditando(null);
    setFormulario(estadoInicial());
    limpiarMensajes();
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleProductoChange = (index, event) => {
    const { name, value, type, checked } = event.target;
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      productos: prev.productos.map((producto, itemIndex) =>
        itemIndex === index
          ? actualizarProductoProveedor(producto, name, type === "checkbox" ? checked : value, productosCatalogo)
          : producto,
      ),
    }));
  };

  const seleccionarProductoCatalogo = (index, value) => {
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      productos: prev.productos.map((producto, itemIndex) =>
        itemIndex === index
          ? actualizarProductoProveedor(producto, "productoCatalogoId", value, productosCatalogo)
          : producto,
      ),
    }));
  };

  const agregarProducto = () => {
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      productos: [...prev.productos, productoVacio()],
    }));
  };

  const quitarProducto = (index) => {
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      productos: prev.productos.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const seleccionarMaterialCatalogo = (index, value) => {
    limpiarMensajes();
    const seleccionado = materialesCatalogo.find((material) => String(material._id) === String(value));
    setFormulario((prev) => ({
      ...prev,
      materiales: prev.materiales.map((material, itemIndex) =>
        itemIndex === index
          ? {
              ...material,
              materialProduccionId: value,
              nombre: seleccionado?.nombre || "",
              unidadMedida: "unidad",
              precioCompraPorUnidad: "",
              activo: true,
            }
          : material,
      ),
    }));
  };

  const handleMaterialChange = (index, event) => {
    const { name, value } = event.target;
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      materiales: prev.materiales.map((material, itemIndex) =>
        itemIndex === index ? { ...material, [name]: value } : material,
      ),
    }));
  };

  const agregarMaterial = () => {
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      materiales: [...prev.materiales, materialVacio()],
    }));
  };

  const quitarMaterial = (index) => {
    limpiarMensajes();
    setFormulario((prev) => ({
      ...prev,
      materiales: prev.materiales.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const prepararPayload = () => ({
    ...formulario,
    tipoDocumento: "CUIT",
    nombreFantasia: "",
    activo: true,
    productos: (formulario.productos || [])
      .filter((producto) => producto.nombre.trim())
      .map((producto) => ({
        ...producto,
        nombre: producto.nombre.trim(),
        codigo: producto.codigo.trim(),
        unidadMedida: producto.unidadMedida || "Unidad",
        precioCompraPorUnidad: Math.max(Number(producto.precioCompraPorUnidad || 0), 0),
        estadoProducto: producto.estadoProducto || "MATERIAPRIMA",
        activo: true,
      })),
    materiales: (formulario.materiales || [])
      .filter((material) => material.nombre.trim())
      .map((material) => ({
        ...material,
        nombre: material.nombre.trim(),
        unidadMedida: "unidad",
        precioCompraPorUnidad: Math.max(Number(material.precioCompraPorUnidad || 0), 0),
        activo: true,
      })),
  });

  const validarFormulario = () => {
    if (!formulario.razonSocial.trim()) {
      setError("La razon social es obligatoria.");
      return false;
    }

    if (formulario.email && !/.+@.+\..+/.test(formulario.email)) {
      setError("El email ingresado no es valido.");
      return false;
    }

    const productoSinCatalogo = (formulario.productos || []).find(
      (producto) => producto.activo !== false && producto.nombre.trim() && !producto.productoCatalogoId,
    );
    if (productoSinCatalogo) {
      setError("Selecciona un producto del catalogo para cada producto del proveedor.");
      return false;
    }

    const materialSinCatalogo = (formulario.materiales || []).find(
      (material) => material.nombre.trim() && !material.materialProduccionId,
    );
    if (materialSinCatalogo) {
      setError("Selecciona un material del catalogo para cada material del proveedor.");
      return false;
    }

    return true;
  };

  const guardarProveedor = async (event) => {
    event.preventDefault();

    if (!validarFormulario()) return;

    try {
      setCargando(true);
      setError("");
      const payload = prepararPayload();

      if (proveedorEditando) {
        await http.put(`/proveedores/${proveedorEditando._id}`, payload);
        setMensaje("Proveedor actualizado correctamente.");
      } else {
        await http.post("/proveedores", payload);
        setMensaje("Proveedor creado correctamente.");
      }

      cerrarModal();
      await obtenerProveedores();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el proveedor.");
    } finally {
      setCargando(false);
    }
  };

  const eliminarProveedor = async (id) => {
    if (!window.confirm("Seguro que queres eliminar este proveedor?")) return;

    try {
      setCargando(true);
      setError("");
      await http.delete(`/proveedores/${id}`);
      setMensaje("Proveedor eliminado correctamente.");
      await obtenerProveedores();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo eliminar el proveedor.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div>
      <div className="page-header actions-only">
        <div className="header-actions">
          <button
            className="btn"
            onClick={() => {
              setProductoCompararId("");
              setComparadorAbierto(true);
            }}
          >
            <ArrowLeftRight size={18} />
            Comparar
          </button>
          <button className="btn btn-primary" onClick={abrirCrear}>
            <Plus size={18} />
            Nuevo proveedor
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && !modalAbierto && <div className="alert alert-error">{error}</div>}

      <section className="panel">
        <div className="toolbar">
          <div>
            <h2>Proveedores cargados</h2>
            <p>Total: {proveedoresFiltrados.length}</p>
          </div>
          <div className="search">
            <Search size={17} />
            <input
              placeholder="Buscar por razon social, documento, ciudad o producto..."
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
            />
          </div>
        </div>

        {cargando ? (
          <div className="loading-state">Cargando proveedores...</div>
        ) : proveedoresFiltrados.length === 0 ? (
          <div className="empty-state">No hay proveedores para mostrar.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Razon social</th>
                  <th>C.U.I.T.</th>
                  <th>Contacto</th>
                  <th>Ubicacion</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proveedoresFiltrados.map((proveedor) => (
                  <tr key={proveedor._id}>
                    <td>
                      <strong>{proveedor.codigoProveedor || "-"}</strong>
                    </td>
                    <td>
                      <strong>{proveedor.razonSocial || "-"}</strong>
                      <span className="subtext">{proveedor.observaciones || "-"}</span>
                    </td>
                    <td>
                      {proveedor.numeroDocumento || "-"}
                    </td>
                    <td>
                      <strong>{proveedor.contacto || "-"}</strong>
                      <span className="subtext">{proveedor.telefonoContacto || proveedor.telefono || "-"}</span>
                      <span className="subtext">{proveedor.email || "-"}</span>
                    </td>
                    <td>
                      <strong>{proveedor.direccion || "-"}</strong>
                      <span className="subtext">{proveedor.ciudad || "-"}</span>
                      <span className="subtext">{proveedor.provincia || "-"}</span>
                    </td>
                    <td>
                      <span className={`badge ${proveedor.activo ? "ok" : "off"}`}>
                        {proveedor.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Detalle"
                          aria-label="Detalle"
                          onClick={() => setProveedorDetalle(proveedor)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Editar"
                          aria-label="Editar"
                          onClick={() => abrirEditar(proveedor)}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="btn btn-danger action-icon-button"
                          type="button"
                          title="Eliminar"
                          aria-label="Eliminar"
                          onClick={() => eliminarProveedor(proveedor._id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        abierto={modalAbierto}
        titulo={proveedorEditando ? "Editar proveedor" : "Cargar nuevo proveedor"}
        subtitulo={
          proveedorEditando
            ? `Modificando: ${proveedorEditando?.razonSocial || ""}`
            : "Completa los datos de identificacion y productos habituales."
        }
        size="wide"
        onClose={cerrarModal}
        footer={
          <>
            <button className="btn" type="button" onClick={cerrarModal}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              form="proveedor-form"
              type="submit"
              disabled={cargando}
            >
              {proveedorEditando ? "Actualizar proveedor" : "Guardar proveedor"}
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <form id="proveedor-form" onSubmit={guardarProveedor}>
          <section className="form-section">
            <h3>IDENTIFICACION</h3>
            <div className="form-grid">
              <Field
                label="RAZON SOCIAL *"
                name="razonSocial"
                value={formulario.razonSocial}
                onChange={handleChange}
              />
              <Field
                label="C.U.I.T."
                name="numeroDocumento"
                value={formulario.numeroDocumento}
                onChange={handleChange}
              />
              <Field
                label="TELEFONO COMERCIAL"
                name="telefono"
                value={formulario.telefono}
                onChange={handleChange}
              />
              <Field
                label="DOMICILIO"
                name="direccion"
                value={formulario.direccion}
                onChange={handleChange}
              />
              <Field
                label="LOCALIDAD"
                name="ciudad"
                value={formulario.ciudad}
                onChange={handleChange}
              />
              <Field
                label="PROVINCIA"
                name="provincia"
                value={formulario.provincia}
                onChange={handleChange}
              />
              <Field
                label="MAIL"
                type="email"
                name="email"
                value={formulario.email}
                onChange={handleChange}
              />
              <Field
                label="CONTACTO"
                name="contacto"
                value={formulario.contacto}
                onChange={handleChange}
              />
              <Field
                label="TELEFONO"
                name="telefonoContacto"
                value={formulario.telefonoContacto}
                onChange={handleChange}
              />
              <Textarea
                label="OBSERVACIONES"
                name="observaciones"
                value={formulario.observaciones}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="form-section">
            <div className="section-title-actions">
              <h3>Productos del proveedor</h3>
              <button className="btn" type="button" onClick={agregarProducto}>
                <Plus size={17} />
                Agregar producto
              </button>
            </div>
            <div className="table-wrap supplier-products-table-wrap">
              <table className="supplier-products-table">
                <thead>
                  <tr>
                    <th>Producto catalogo</th>
                    <th>Nombre</th>
                    <th>Codigo</th>
                    <th>Unidad</th>
                    <th>Costo de compra</th>
                    <th>Quitar</th>
                  </tr>
                </thead>
                <tbody>
                  {formulario.productos.length === 0 ? (
                    <tr><td colSpan="6" className="empty-inline">Sin productos cargados.</td></tr>
                  ) : formulario.productos.map((producto, index) => (
                    <tr key={producto._id || index}>
                      <td>
                        <SearchableSelect
                          compact
                          value={producto.productoCatalogoId || ""}
                          onChange={(value) => seleccionarProductoCatalogo(index, value)}
                          options={productosCatalogo.map((item) => ({
                            value: item._id,
                            label: `${item.codigoSku} - ${item.nombre}`,
                          }))}
                        />
                      </td>
                      <td><Field compact name="nombre" value={producto.nombre} onChange={(event) => handleProductoChange(index, event)} /></td>
                      <td><Field compact name="codigo" value={producto.codigo} onChange={(event) => handleProductoChange(index, event)} /></td>
                      <td><Field compact value={producto.unidadMedida} disabled /></td>
                      <td>
                        <Field
                          compact
                          type="number"
                          min="0"
                          step="0.01"
                          name="precioCompraPorUnidad"
                          value={producto.precioCompraPorUnidad}
                          title={`Costo por 1 ${unidadCostoDe(
                            productosCatalogo.find((item) => String(item._id) === idCatalogoDe(producto)),
                            producto,
                          )}`}
                          onChange={(event) => handleProductoChange(index, event)}
                        />
                      </td>
                      <td>
                        <button className="btn btn-danger action-icon-button" type="button" title="Quitar" aria-label="Quitar" onClick={() => quitarProducto(index)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="form-section">
            <div className="section-title-actions">
              <h3>Materiales de producción del proveedor</h3>
              <button className="btn" type="button" onClick={agregarMaterial}>
                <Plus size={17} />
                Agregar material
              </button>
            </div>
            <div className="supplier-products">
              {formulario.materiales.length === 0 ? (
                <div className="empty-inline">Sin materiales cargados.</div>
              ) : (
                formulario.materiales.map((material, index) => (
                  <div className="supplier-product-row supplier-product-row-materials" key={material._id || index}>
                    <SearchableSelect
                      label="Material de producción"
                      value={material.materialProduccionId || ""}
                      onChange={(value) => seleccionarMaterialCatalogo(index, value)}
                      options={materialesCatalogo.map((item) => ({
                        value: item._id,
                        label: item.nombre,
                      }))}
                    />
                    <Field label="Nombre" value={material.nombre} disabled />
                    <Field label="Unidad" value={material.unidadMedida} disabled />
                    <Field
                      label="Costo de compra por unidad"
                      type="number"
                      min="0"
                      step="0.01"
                      name="precioCompraPorUnidad"
                      value={material.precioCompraPorUnidad}
                      onChange={(event) => handleMaterialChange(index, event)}
                    />
                    <button
                      className="btn btn-danger product-remove"
                      type="button"
                      onClick={() => quitarMaterial(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        </form>
      </Modal>

      <Modal
        abierto={Boolean(proveedorDetalle)}
        titulo="Detalle del proveedor"
        subtitulo={proveedorDetalle?.razonSocial || ""}
        size="wide"
        onClose={() => setProveedorDetalle(null)}
        footer={
          <button className="btn" type="button" onClick={() => setProveedorDetalle(null)}>
            Cerrar
          </button>
        }
      >
        {proveedorDetalle && (
          <div className="order-detail">
            <section className="order-detail-summary">
              <DetalleDato label="Codigo" value={proveedorDetalle.codigoProveedor} />
              <DetalleDato label="Razon social" value={proveedorDetalle.razonSocial} />
              <DetalleDato
                label="Documento"
                value={`${proveedorDetalle.tipoDocumento || ""} ${proveedorDetalle.numeroDocumento || ""}`.trim()}
              />
              <DetalleDato label="Telefono comercial" value={proveedorDetalle.telefono} />
              <DetalleDato label="Contacto" value={proveedorDetalle.contacto} />
              <DetalleDato label="Telefono de contacto" value={proveedorDetalle.telefonoContacto} />
              <DetalleDato label="Email" value={proveedorDetalle.email} />
              <DetalleDato label="Direccion" value={proveedorDetalle.direccion} />
              <DetalleDato label="Localidad" value={proveedorDetalle.ciudad} />
              <DetalleDato label="Provincia" value={proveedorDetalle.provincia} />
              <DetalleDato label="Estado" value={proveedorDetalle.activo ? "Activo" : "Inactivo"} />
            </section>
            <section className="order-detail-section">
              <h3>Productos asociados</h3>
              {(proveedorDetalle.productos || []).filter((producto) => producto.activo !== false).length === 0 ? (
                <div className="empty-inline">Sin productos asociados.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Codigo</th>
                        <th>Unidad</th>
                        <th>Costo de compra</th>
                        <th>Historial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(proveedorDetalle.productos || [])
                        .filter((producto) => producto.activo !== false)
                        .map((producto) => (
                          <tr key={producto._id || producto.nombre}>
                            <td>{producto.nombre || "-"}</td>
                            <td>{producto.codigo || "-"}</td>
                            <td>{producto.unidadMedida || "-"}</td>
                            <td>
                              {formatearPrecio(
                                producto.precioCompraPorUnidad ?? producto.precioPorKg,
                              )}{" "}
                              / {unidadCostoDe(
                                productosCatalogo.find(
                                  (item) => String(item._id) === idCatalogoDe(producto),
                                ),
                                producto,
                              )}
                            </td>
                            <td>
                              <button
                                className="btn"
                                type="button"
                                onClick={() => abrirHistorialPrecio(proveedorDetalle, producto)}
                              >
                                <History size={16} />
                                Ver historial
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section className="order-detail-section">
              <h3>Materiales de producción asociados</h3>
              {(proveedorDetalle.materiales || []).filter((material) => material.activo !== false).length === 0 ? (
                <div className="empty-inline">Sin materiales asociados.</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th>Unidad</th>
                        <th>Costo de compra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(proveedorDetalle.materiales || [])
                        .filter((material) => material.activo !== false)
                        .map((material) => (
                          <tr key={material._id || material.nombre}>
                            <td>{material.nombre || "-"}</td>
                            <td>{material.unidadMedida || "unidad"}</td>
                            <td>{formatearPrecio(material.precioCompraPorUnidad)} / unidad</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section className="order-detail-notes">
              <h3>Observaciones</h3>
              <p>{proveedorDetalle.observaciones || "Sin observaciones."}</p>
            </section>
          </div>
        )}
      </Modal>

      <Modal
        abierto={comparadorAbierto}
        titulo="Comparar costos de compra"
        subtitulo="Consulta los proveedores disponibles para un producto."
        size="comparison"
        onClose={() => setComparadorAbierto(false)}
        footer={
          <button className="btn" type="button" onClick={() => setComparadorAbierto(false)}>
            Cerrar
          </button>
        }
      >
        <div className="cost-comparison">
          <SearchableSelect
            label="Producto"
            value={productoCompararId}
            onChange={(value) => {
              const seleccionado = productosCatalogo.find(
                (producto) => String(producto._id) === String(value),
              );
              setProductoCompararId(value);
              setCantidadSimulacion(String(seleccionado?.recetaRendimiento?.cantidad || 1));
              setMargenSimulacion(String(seleccionado?.margenGananciaPorcentaje || 0));
              setProveedoresSimulacion({});
            }}
            placeholder="Buscar por nombre o SKU..."
            options={productosCatalogo.map((producto) => ({
              value: producto._id,
              label: `${producto.codigoSku} - ${producto.nombre}`,
            }))}
          />

          {!productoComparado ? (
            <div className="empty-inline">Selecciona un producto para comparar sus costos.</div>
          ) : ["Intermedio", "Final"].includes(productoComparado.tipo) ? (
            <div className="recipe-cost-simulation">
              <div className="simulation-controls">
                <div className="field">
                  <label htmlFor="cantidad-simulacion">
                    Cantidad a producir ({productoComparado.recetaRendimiento?.unidadMedida || productoComparado.unidadMedida})
                  </label>
                  <input
                    id="cantidad-simulacion"
                    type="number"
                    min="0.000001"
                    step="any"
                    value={cantidadSimulacion}
                    onChange={(event) => setCantidadSimulacion(event.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="margen-simulacion">Margen de ganancia (%)</label>
                  <input
                    id="margen-simulacion"
                    type="number"
                    min="0"
                    step="0.01"
                    value={margenSimulacion}
                    onChange={(event) => setMargenSimulacion(event.target.value)}
                  />
                </div>
                <div className="simulation-output">
                  <span>Rendimiento de la receta</span>
                  <strong>
                    {formatearCantidad(productoComparado.recetaRendimiento?.cantidad)}{" "}
                    {productoComparado.recetaRendimiento?.unidadMedida || productoComparado.unidadMedida}
                  </strong>
                  <small>
                    {formatearCantidad(simulacionReceta?.cantidadRecetas)} producciones de receta
                  </small>
                </div>
              </div>

              {simulacionReceta?.errores.map((mensajeError) => (
                <div className="alert alert-error" key={mensajeError}>{mensajeError}</div>
              ))}

              {simulacionReceta?.lineas.length === 0 ? (
                <div className="empty-inline">La receta no tiene componentes para calcular.</div>
              ) : (
                <div className="table-wrap simulation-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Componente</th>
                        <th>Tipo</th>
                        <th>Cantidad necesaria</th>
                        <th>Proveedor</th>
                        <th>Costo unitario</th>
                        <th>Costo total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulacionReceta?.lineas.map((linea) => (
                        <tr key={linea.clave}>
                          <td><strong>{linea.nombre}</strong></td>
                          <td>{linea.tipo === "material" ? "Material de produccion" : "Materia prima"}</td>
                          <td>{formatearCantidad(linea.cantidad)} {linea.unidad}</td>
                          <td>
                            {linea.opciones.length > 0 ? (
                              <select
                                className="simulation-provider-select"
                                aria-label={`Proveedor para ${linea.nombre}`}
                                value={linea.seleccionada?.proveedor?._id || ""}
                                onChange={(event) =>
                                  setProveedoresSimulacion((actual) => ({
                                    ...actual,
                                    [linea.clave]: event.target.value,
                                  }))
                                }
                              >
                                {linea.opciones.map((opcion) => (
                                  <option key={opcion.proveedor._id} value={opcion.proveedor._id}>
                                    {opcion.proveedor.razonSocial}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="missing-cost">Sin proveedor asociado</span>
                            )}
                          </td>
                          <td>
                            {formatearPrecio(linea.seleccionada?.precio)}
                            <span className="subtext">por {linea.unidad}</span>
                          </td>
                          <td><strong>{formatearImporte(linea.costoTotal)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {simulacionReceta?.faltantes.length > 0 && (
                <div className="simulation-warning">
                  El total es parcial: {simulacionReceta.faltantes.length} componente(s) no tienen un precio valido.
                </div>
              )}

              <div className="simulation-totals">
                <div>
                  <span>Ingredientes</span>
                  <strong>{formatearImporte(simulacionReceta?.costoIngredientes)}</strong>
                </div>
                <div>
                  <span>Materiales</span>
                  <strong>{formatearImporte(simulacionReceta?.costoMateriales)}</strong>
                </div>
                <div className="simulation-grand-total">
                  <span>Costo total</span>
                  <strong>{formatearImporte(simulacionReceta?.costoTotal)}</strong>
                </div>
                <div>
                  <span>
                    Margen ({formatearCantidad(simulacionReceta?.margenGananciaPorcentaje, 2)}%)
                  </span>
                  <strong>{formatearImporte(simulacionReceta?.importeMargen)}</strong>
                </div>
                <div className="simulation-sale-price">
                  <span>Precio de venta calculado</span>
                  <strong>{formatearImporte(simulacionReceta?.precioVentaCalculado)}</strong>
                </div>
              </div>
            </div>
          ) : comparacionProveedores.length === 0 ? (
            <div className="empty-inline">Ningun proveedor tiene asociado este producto.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th>C.U.I.T.</th>
                    <th>Contacto</th>
                    <th>{`Costo por 1 ${unidadCostoDe(productoComparado)}`}</th>
                  </tr>
                </thead>
                <tbody>
                  {comparacionProveedores.map(({ proveedor, producto }, index) => {
                    const precio = producto.precioCompraPorUnidad ?? producto.precioPorKg;
                    return (
                      <tr key={proveedor._id}>
                        <td>
                          <strong>{proveedor.razonSocial}</strong>
                          {index === 0 && Number(precio) > 0 && <span className="subtext best-price">Mejor precio</span>}
                        </td>
                        <td>{proveedor.numeroDocumento || "-"}</td>
                        <td>
                          {proveedor.contacto || "-"}
                          <span className="subtext">{proveedor.telefonoContacto || proveedor.telefono || "-"}</span>
                        </td>
                        <td><strong>{formatearPrecio(precio)}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        abierto={Boolean(historialPrecio)}
        titulo={`Historial de precios: ${historialPrecio?.producto?.nombre || ""}`}
        subtitulo={historialPrecio?.proveedor?.razonSocial || ""}
        size="wide"
        onClose={() => setHistorialPrecio(null)}
        footer={
          <button className="btn" type="button" onClick={() => setHistorialPrecio(null)}>
            Cerrar
          </button>
        }
      >
        <div className="price-history">
          <div className="price-history-summary">
            <DetalleDato
              label="Precio actual"
              value={`${formatearPrecio(historialPrecio?.producto?.precioActual)} / ${
                historialPrecio?.producto?.unidadCosto === "l" ? "L" : "kg"
              }`}
            />
            <DetalleDato label="Cambios registrados" value={historialFiltrado.length} />
          </div>

          <div className="finalized-filter price-history-filter">
            <input
              aria-label="Historial desde"
              type="date"
              value={filtroHistorial.desde}
              onChange={(event) => setFiltroHistorial((actual) => ({ ...actual, desde: event.target.value }))}
            />
            <input
              aria-label="Historial hasta"
              type="date"
              value={filtroHistorial.hasta}
              onChange={(event) => setFiltroHistorial((actual) => ({ ...actual, hasta: event.target.value }))}
            />
            <button
              className="btn"
              type="button"
              onClick={() => setFiltroHistorial({ desde: "", hasta: "" })}
              title="Limpiar rango"
            >
              <RotateCcw size={16} />
              Limpiar
            </button>
          </div>

          {historialError && <div className="alert alert-error">{historialError}</div>}
          {historialCargando ? (
            <div className="loading-state">Cargando historial...</div>
          ) : historialFiltrado.length === 0 ? (
            <div className="empty-state">No hay cambios de precio para el rango seleccionado.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Precio anterior</th>
                    <th>Precio nuevo</th>
                    <th>Variacion</th>
                    <th>Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {historialFiltrado.map((movimiento) => {
                    const anterior = movimiento.precioAnterior;
                    const variacion = anterior === null || anterior === undefined
                      ? null
                      : Number(movimiento.precioNuevo || 0) - Number(anterior || 0);
                    return (
                      <tr key={movimiento._id}>
                        <td>{formatearFechaHora(movimiento.fechaCambio || movimiento.createdAt)}</td>
                        <td>{anterior === null || anterior === undefined ? "-" : formatearPrecio(anterior)}</td>
                        <td><strong>{formatearPrecio(movimiento.precioNuevo)}</strong></td>
                        <td className={variacion > 0 ? "price-up" : variacion < 0 ? "price-down" : ""}>
                          {variacion === null ? "Precio inicial" : formatearVariacion(variacion)}
                        </td>
                        <td>{movimiento.usuario || "Sistema"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function DetalleDato({ label, value }) {
  return (
    <div className="detail-item">
      <span>{label}</span>
      <strong>{value === undefined || value === null || value === "" ? "-" : value}</strong>
    </div>
  );
}

function Field({ label, compact = false, ...props }) {
  return (
    <div className={`field ${compact ? "field-compact" : ""}`}>
      {!compact && <label>{label}</label>}
      <input type="text" {...props} />
    </div>
  );
}

function SearchableSelect({ label, value, options, onChange, placeholder = "Buscar...", compact = false }) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const seleccion = options.find((option) => String(getOptionValue(option)) === String(value || ""));
  const query = texto.trim().toLowerCase();
  const filtradas = options
    .filter((option) => getOptionLabel(option).toLowerCase().includes(query))
    .slice(0, 12);

  useEffect(() => {
    setTexto(seleccion ? getOptionLabel(seleccion) : "");
  }, [value, seleccion]);

  const elegir = (option) => {
    setTexto(getOptionLabel(option));
    setAbierto(false);
    onChange(getOptionValue(option), option);
  };

  return (
    <div className={`field searchable-select ${compact ? "field-compact" : ""}`}>
      {!compact && <label>{label}</label>}
      <input
        type="text"
        value={texto}
        placeholder={placeholder}
        onFocus={() => setAbierto(true)}
        onBlur={() => window.setTimeout(() => setAbierto(false), 120)}
        onChange={(event) => {
          setTexto(event.target.value);
          setAbierto(true);
          onChange("", null);
        }}
        autoComplete="off"
      />
      {abierto && (
        <div className="searchable-options">
          {filtradas.length === 0 ? (
            <div className="searchable-empty">Sin resultados</div>
          ) : (
            filtradas.map((option) => (
              <button
                key={getOptionValue(option)}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => elegir(option)}
              >
                {getOptionLabel(option)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}



function Textarea({ label, ...props }) {
  return (
    <div className="field textarea-field">
      <label>{label}</label>
      <textarea {...props} />
    </div>
  );
}
